/**
 * Strategic fit score display card.
 *
 * AC-015: Shows pairwise fit scores
 * AC-016: Score displayed with color coding
 * AC-017: "(partial)" label when data is incomplete
 */

import { useState } from 'react';
import type { StrategicFitScore } from '../../types';

interface FitScoreCardProps {
  fitScores: StrategicFitScore[];
}

const DIMENSION_LABELS: Record<string, string> = {
  market_overlap: 'Market Overlap',
  product_complementarity: 'Product Complementarity',
  size_compatibility: 'Size Compatibility',
  geographic_proximity: 'Geographic Proximity',
  data_confidence: 'Data Confidence',
};

function getScoreColor(score: number): string {
  if (score >= 70) return 'text-green-600';
  if (score >= 40) return 'text-amber-600';
  return 'text-red-600';
}

function getScoreBgColor(score: number): string {
  if (score >= 70) return 'bg-green-50 border-green-200';
  if (score >= 40) return 'bg-amber-50 border-amber-200';
  return 'bg-red-50 border-red-200';
}

function getBarColor(score: number): string {
  if (score >= 70) return 'bg-green-500';
  if (score >= 40) return 'bg-amber-500';
  return 'bg-red-500';
}

function FitScoreItem({ score }: { score: StrategicFitScore }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`rounded-lg border p-4 ${getScoreBgColor(score.overall_score)}`}
      data-testid="fit-score-card"
    >
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-gray-700">
          {score.company_a_name} <span className="text-gray-400 mx-1">&harr;</span>{' '}
          {score.company_b_name}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-lg font-bold ${getScoreColor(score.overall_score)}`}>
            {Math.round(score.overall_score)}/100
          </span>
          {score.is_partial && <span className="text-xs text-gray-500">(partial)</span>}
        </div>
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline"
      >
        {expanded ? 'Hide details' : 'Show details'}
      </button>
      {expanded && (
        <div className="mt-3 space-y-2" data-testid="fit-score-details">
          {Object.entries(score.dimension_scores).map(([dim, value]) => (
            <div key={dim} className="flex items-center gap-2">
              <span className="text-xs text-gray-600 w-40">{DIMENSION_LABELS[dim] || dim}</span>
              <div className="flex-1 h-2 rounded-full bg-gray-200">
                <div
                  className={`h-2 rounded-full ${getBarColor(value)}`}
                  style={{ width: `${Math.min(100, value)}%` }}
                />
              </div>
              <span className="text-xs font-medium text-gray-600 w-8 text-right">
                {Math.round(value)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FitScoreCard({ fitScores }: FitScoreCardProps) {
  if (fitScores.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="fit-scores-section">
      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Strategic Fit</h3>
      {fitScores.map((score) => (
        <FitScoreItem key={`${score.company_a_id}-${score.company_b_id}`} score={score} />
      ))}
    </div>
  );
}
